import type { Transfer } from './useFileUpload';

interface Props {
  transfers: Transfer[];
  dragActive: boolean;
  onCancel: (id: string) => void;
  onClear: () => void;
}

/**
 * Two visual elements:
 *   1. Full-surface drop indicator (only when something is dragged into the page).
 *   2. Floating panel listing active and recent transfers with progress, throughput, and a
 *      cancel button.
 */
export function FileTransferOverlay({ transfers, dragActive, onCancel, onClear }: Props) {
  const visible = transfers.length > 0;
  return (
    <>
      {dragActive && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
          <div className="bg-[var(--color-accent)]/15 border-2 border-dashed border-[var(--color-accent)] rounded-lg px-8 py-12 text-center">
            <div className="text-3xl mb-2">↓</div>
            <div className="text-[var(--color-accent)] text-sm uppercase tracking-wider">
              Drop file to send to remote
            </div>
          </div>
        </div>
      )}
      {visible && (
        <div className="absolute bottom-3 left-3 z-20 max-w-md w-full sm:w-96 bg-[var(--color-surface)]/95 border border-[var(--color-border)] rounded-md p-3 backdrop-blur">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
              File transfers
            </span>
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              clear finished
            </button>
          </div>
          <ul className="space-y-2 max-h-60 overflow-y-auto">
            {transfers.map((t) => (
              <li key={t.id} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate flex-1" title={t.name}>{t.name}</span>
                  <span className="font-mono text-[var(--color-text-muted)]">
                    {formatBytes(t.size)}
                  </span>
                  {(t.state.kind === 'sending' || t.state.kind === 'pending') && (
                    <button
                      type="button"
                      onClick={() => onCancel(t.id)}
                      className="text-[var(--color-danger)] hover:underline"
                    >
                      cancel
                    </button>
                  )}
                </div>
                <ProgressLine t={t} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function ProgressLine({ t }: { t: Transfer }) {
  const pct =
    t.state.kind === 'sending'
      ? Math.min(100, Math.round((t.state.sent / Math.max(1, t.state.total)) * 100))
      : t.state.kind === 'done'
        ? 100
        : t.state.kind === 'finalising'
          ? 100
          : 0;
  const bar =
    t.state.kind === 'failed'
      ? 'bg-[var(--color-danger)]'
      : t.state.kind === 'done'
        ? 'bg-[var(--color-accent)]'
        : 'bg-[var(--color-text-muted)]';
  return (
    <div className="mt-1">
      <div className="h-1 bg-[var(--color-border)] rounded overflow-hidden">
        <div className={`h-1 ${bar}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between mt-0.5 text-[10px] text-[var(--color-text-muted)] font-mono">
        <span>
          {t.state.kind === 'pending' && 'waiting…'}
          {t.state.kind === 'sending' && `${pct}% · ${t.bps ? formatBps(t.bps) : '...'}`}
          {t.state.kind === 'finalising' && 'verifying…'}
          {t.state.kind === 'done' && '✓ saved on host'}
          {t.state.kind === 'failed' && `× ${t.state.error}`}
        </span>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MiB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GiB`;
}

function formatBps(bps: number): string {
  const Bps = bps;
  if (Bps < 1024 * 1024) return `${(Bps / 1024).toFixed(0)} KiB/s`;
  return `${(Bps / 1024 / 1024).toFixed(1)} MiB/s`;
}
